/**
 * iter86.31.1 — Golf Standings tab (UI-only view over tournament.teams).
 *
 * Full-field golfer table: position, current-round score, thru hole,
 * total strokes, average per FINISHED round. The viewer's picked golfers
 * highlight yellow. Missed cut / WD / DQ rows get a crossed-out label.
 * Filter: Top 10 (default) / Top 30 / All.
 */
import { useMemo, useState } from "react";
import { ListOrdered } from "lucide-react";

const OUT_LABELS = { cut: "CUT", wd: "WD", dq: "DQ", dnf: "DNF", withdrawn: "WD", disqualified: "DQ" };

export default function GolfStandings({ tournament, myTeamIds }) {
    const [filter, setFilter] = useState("top10");
    const golf = tournament?.golf || {};
    const mySet = useMemo(() => new Set(myTeamIds || []), [myTeamIds]);

    const rows = useMemo(() => {
        const teams = [...(tournament?.teams || [])];
        // iter86.61 — Merge context-only field_extras (golfers on the
        // real leaderboard who fell outside our top-30 pool) so the
        // Field Standings tab shows the actual tournament field, not
        // just the draftable pool. These rows aren't scored/picked —
        // they render dimmer with a "FIELD" tag.
        const extras = (tournament?.golf?.field_extras || []).map((fx) => ({
            ...fx,
            _extra: true,
        }));
        const merged = [...teams, ...extras];
        merged.sort((a, b) => {
            const pa = a.golf?.position_num, pb = b.golf?.position_num;
            if (pa != null && pb != null) return pa - pb;
            if (pa != null) return -1;
            if (pb != null) return 1;
            return (a.seed || 999) - (b.seed || 999);
        });
        return merged;
    }, [tournament?.teams, tournament?.golf?.field_extras]);

    const visible = filter === "top10" ? rows.slice(0, 10) : filter === "top30" ? rows.slice(0, 30) : rows;
    const started = (golf.current_round || 0) >= 1 && (golf.round_status || "Not Started") !== "Not Started";

    return (
        <div data-testid="golf-standings">
            {/* iter87.26 — outer brutal-card + "Field Standings" h3
                dropped. Takeover shell already ships the "Standings"
                title. Filter select stays right-aligned. */}
            <div className="flex items-center justify-end mb-4 gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-widest hidden sm:inline">Show</span>
                    <select
                        data-testid="golf-standings-filter"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="brutal-btn-ghost text-xs py-1.5 px-2 bg-ink border-[#333] cursor-pointer"
                    >
                        <option value="top10">Top 10</option>
                        <option value="top30">Top 30</option>
                        <option value="all">All golfers</option>
                    </select>
                </div>
            </div>
            <p className="text-[10px] text-zinc-500 mb-3">
                {started
                    ? <>Round {golf.current_round} — {golf.round_status}. <span className="text-cyber">Your golfers highlighted.</span></>
                    : <>Tournament hasn't started — golfers listed by world ranking. <span className="text-cyber">Your golfers highlighted.</span></>}
            </p>

            {/* Header — iter87.165: fixed-width numeric columns so
                Pos / Rd Score / Hole / Tot Strokes / Avg align in the
                same pixel gutter across the leaderboard family
                (LeaderboardTable + GolfLeaderboard + GolfStandings).
                Grid template: 56px pos, flex-name, 72px score, 56px hole,
                88px strokes, 72px avg. Overflow wrapper keeps narrow
                phones scrollable. */}
            <div className="overflow-x-auto">
            <div className="min-w-[500px]">
            <div
                className="grid gap-3 px-2 pb-1.5 border-b-2 border-[#333] text-[9px] uppercase tracking-widest text-zinc-500 font-bold"
                style={{ gridTemplateColumns: "56px minmax(120px,1fr) 72px 56px 88px 72px" }}
            >
                <div>Pos</div>
                <div>Golfer</div>
                <div className="text-right">Rd Score</div>
                <div className="text-right">Hole</div>
                <div className="text-right">Tot Strokes</div>
                <div className="text-right">Avg / Rd</div>
            </div>
            <div className="divide-y divide-[#1c1c1c]">
                {visible.map((t) => {
                    const g = t.golf || {};
                    const mine = mySet.has(t.id);
                    const isExtra = !!t._extra;
                    const outLabel = OUT_LABELS[(g.status || "").toLowerCase()];
                    const rp = g.rounds_played || 0;
                    const avg = rp > 0 ? (g.total_strokes / rp).toFixed(2) : "—";
                    return (
                        <div
                            key={t.id}
                            data-testid={`golf-standings-row-${t.id}`}
                            className={`grid gap-3 items-center px-2 py-2 text-xs ${
                                mine ? "bg-cyber/15 border-l-4 border-cyber" : ""
                            } ${isExtra ? "opacity-60" : ""}`}
                            style={{ gridTemplateColumns: "56px minmax(120px,1fr) 72px 56px 88px 72px" }}
                        >
                            <div className="font-mono font-bold text-zinc-300">
                                {started ? (g.position || "—") : (isExtra ? "—" : `#${t.seed}`)}
                            </div>
                            <div className="flex items-center gap-1.5 min-w-0">
                                <span className="shrink-0">{t.flag || "⛳"}</span>
                                <span className={`truncate font-bold ${outLabel ? "line-through text-zinc-500" : mine ? "text-cyber" : "text-zinc-100"}`}>
                                    {t.name}
                                </span>
                                {outLabel && (
                                    <span className="pill bg-blaze/15 border-blaze text-blaze text-[8px] font-bold px-1 py-0" data-testid={`golf-standings-out-${t.id}`}>
                                        {outLabel}
                                    </span>
                                )}
                                {mine && <span className="text-[8px] text-cyber font-bold uppercase shrink-0">Mine</span>}
                                {isExtra && (
                                    <span
                                        className="pill bg-zinc-800 border-zinc-600 text-zinc-400 text-[8px] font-bold px-1 py-0 uppercase tracking-widest"
                                        data-testid={`golf-standings-extra-${t.id}`}
                                        title="On the leaderboard but outside the drafted pool"
                                    >
                                        Field
                                    </span>
                                )}
                            </div>
                            <div className="text-right font-mono text-zinc-300">
                                {outLabel ? "—" : (g.current_round_score ?? "—")}
                            </div>
                            <div className="text-right font-mono text-zinc-500">
                                {outLabel ? "—" : (g.thru ?? g.current_hole ?? "—")}
                            </div>
                            <div className="text-right font-mono text-zinc-200">
                                {rp > 0 ? g.total_strokes : "—"}
                                {g.total_to_par != null && rp > 0 && (
                                    <span className="block text-[9px] text-zinc-600">{g.total_to_par}</span>
                                )}
                            </div>
                            <div className="text-right font-mono font-bold text-white">{avg}</div>
                        </div>
                    );
                })}
            </div>
            </div>
            </div>
            {visible.length === 0 && (
                <p className="text-zinc-500 text-sm text-center py-6">No golfers in the field yet.</p>
            )}
        </div>
    );
}
